/**
 * Scheme checking for link and image targets, shared by every renderer that
 * writes markup into the DOM.
 *
 * The allowlist is `http`, `https` and `mailto`, plus targets that carry no
 * scheme at all (relative paths, fragments, queries, protocol-relative URLs).
 * `tel:` is absent on purpose.
 *
 * It is deliberately short, and this is not the only place where the schemes
 * allowed for this content are decided. Before changing the list, find the
 * other place and change it there too - a scheme allowed here and dropped
 * there, or the other way round, is a difference nobody sees until a link
 * stops working.
 */
import { escape } from 'lodash'

const ALLOWED_SCHEMES = ['http', 'https', 'mailto']

/**
 * Code points a browser drops before it parses a target, so a scheme can hide
 * behind them or inside them: the C0 controls (NUL, tab, newline, carriage
 * return), space, DEL, and the Unicode spaces that a hand-written `\s` class
 * misses. Spelled out as numbers rather than as a regular expression - a
 * character class of invisible characters is unreadable and easy to break.
 */
const IGNORED_WHEN_PARSING = new Set<number>([
  ...Array.from({ length: 0x21 }, (_unused, index) => index), // 0x00-0x20: C0 controls and space
  0x7f, // DEL
  0xa0, // no-break space
  0x1680, // ogham space mark
  ...Array.from({ length: 11 }, (_unused, index) => 0x2000 + index), // en quad .. hair space
  0x2028, // line separator
  0x2029, // paragraph separator
  0x202f, // narrow no-break space
  0x205f, // medium mathematical space
  0x3000, // ideographic space
  0xfeff // zero width no-break space
])

const stripIgnoredCharacters = (value: string): string =>
  Array.from(value)
    .filter((character) => !IGNORED_WHEN_PARSING.has(character.codePointAt(0) as number))
    .join('')

// The pattern takes any run of digits with the semicolon optional, so the
// number it yields is unbounded, while `String.fromCodePoint` raises for
// anything above the highest code point Unicode defines.
const HIGHEST_CODE_POINT = 0x10ffff
const REPLACEMENT_CHARACTER = '\ufffd'
const NUMERIC_REFERENCE = /&#(x)?([0-9a-f]+);?/gi
const NAMED_REFERENCES: { [key: string]: string } = {
  colon: ':',
  tab: '\t',
  newline: '\n'
}
const NAMED_REFERENCE = new RegExp(`&(${Object.keys(NAMED_REFERENCES).join('|')});?`, 'gi')

/**
 * Turns the character references that can hide a scheme back into characters.
 *
 * This runs on the value we CHECK, never on the value we write out. Where the
 * value comes from a parsed attribute the parser has already decoded it and
 * this is belt and braces; where it comes from a raw string it is what makes a
 * scheme spelled with a reference resolve to the same scheme as the plain
 * spelling.
 *
 * What it is for: a scheme written with character references should resolve to
 * the scheme the allowlist compares, so that spelling it that way is not a way
 * past the list. It says nothing about what happens to the value afterwards -
 * every caller hands the browser the value it checked, verbatim, and none of
 * them decodes it again.
 */
const decodeSchemeHidingReferences = (value: string): string =>
  value
    .replace(NUMERIC_REFERENCE, (_match, hex, digits) => {
      const code = parseInt(digits, hex ? 16 : 10)
      // Both branches end in the replacement character, for two different
      // reasons.
      //
      // Past the highest code point, that is what an HTML parser puts there
      // too. Digits that do not parse at all are a different case: to a parser
      // those are not a reference and stay as text, so treating them this way
      // is stricter than a browser. That is the harmless direction and it is
      // deliberate - a value we cannot read is not a value we want to compare
      // against the allowlist.
      //
      // What neither branch may do is leave the match as it stands. It carries
      // a `#`, which is read as the start of a fragment further down, so a
      // target whose scheme sits behind such a reference would come out
      // looking relative. The replacement character has no meaning there, so
      // the scheme that follows it stays a scheme and gets compared.
      if (Number.isNaN(code) || code > HIGHEST_CODE_POINT) {
        return REPLACEMENT_CHARACTER
      }

      return String.fromCodePoint(code)
    })
    .replace(NAMED_REFERENCE, (match, name) => NAMED_REFERENCES[name.toLowerCase()] ?? match)

/**
 * Whether a target may be written into an `href` or a `src`.
 *
 * The chain is: decode, normalise, check - and whoever renders the value writes
 * the ESCAPED original, not what was checked here. Checking the escaped form
 * instead would make the same input behave differently depending on the path it
 * took to get here.
 *
 * An empty or missing target is "safe": there is nothing to point anywhere.
 */
export const isSafeTarget = (value?: string | null): boolean => {
  if (!value) {
    return true
  }

  const candidate = stripIgnoredCharacters(decodeSchemeHidingReferences(value))
  const colon = candidate.indexOf(':')
  if (colon === -1) {
    return true
  }

  // A colon behind the first `/`, `?` or `#` belongs to the path, the query or
  // the fragment - `/wiki/a:b` is a relative link, not a scheme called `/wiki/a`.
  const separator = candidate.search(/[/?#]/)
  if (separator !== -1 && separator < colon) {
    return true
  }

  return ALLOWED_SCHEMES.includes(candidate.slice(0, colon).toLowerCase())
}

/**
 * Escapes text that is about to be written into the DOM as markup.
 *
 * Callers that also linkify must escape FIRST and linkify afterwards, or they
 * escape the anchors they just built.
 */
export const escapeForMarkup = (value: string): string => escape(value)

/**
 * Drops link and image targets we do not allow from a fragment of markup.
 *
 * The attributes are read through a parser rather than matched in the string.
 * Raw HTML keeps whatever quoting its author used - unquoted, single-quoted, or
 * broken across lines - and a pattern written against one of those spellings
 * silently misses the others. A parser sees the value, not the spelling.
 *
 * Everything else is left as it was, with one exception, and it is a property
 * of the parser rather than a decision: the fragment is parsed as a document,
 * so a `<style>`, a `<script>` or a comment at the very START of the markup
 * ends up in the head and is gone once the body is serialised back. The same
 * things inside the text survive. For a script that is harmless here; for a
 * style or a comment it is silent loss.
 *
 * ⚠️ A leading `<noscript>` is measured to differ between the two parsers this
 * code meets: Chromium 131's DOMParser keeps its text and drops its tags,
 * jsdom 15 drops the whole element. **A spec asserting either shape would pin
 * one of the two and not the other** - which is why there is none, and why
 * this is written down here instead.
 *
 * Only `href` and `src` are touched: this removes no tag and no other
 * attribute. That is deliberate and it is narrow. Stripping tags here would
 * also take the shortcode placeholders that the markdown renderers inject and
 * then look up again by `data-shortcode-id`, and every image and video in
 * every markdown post would disappear without an error - no exception, no
 * failing test, just a post with something missing.
 */
export const dropUnsafeTargets = (html: string): string => {
  if (!html) {
    return html
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html')

  parsed.body.querySelectorAll('[href], [src]').forEach((element) => {
    if (element.hasAttribute('href') && !isSafeTarget(element.getAttribute('href'))) {
      element.removeAttribute('href')
    }
    if (element.hasAttribute('src') && !isSafeTarget(element.getAttribute('src'))) {
      element.removeAttribute('src')
    }
  })

  return parsed.body.innerHTML
}
